<template>
  <VRow
    data-testid="nav"
    class="nav-component shrink"
    :class="{ 'nav-component--drawer-open': isSideMenuOpen, 'nav-component--static': staticHeader }"
    no-gutters
  >
    <VToolbar
      class="navbar px-2 pr-4 px-sm-4 color-customer"
      :class="[ cartStore.isSharedCartMode ? 'shared-cart-top' : undefined, search ? 'searchbar' : undefined, scrolled ? 'navbar--scrolled' : undefined, activeLayer === 'rewards' ? 'rewards-flyout-open' : undefined ]"
      density="default"
      flat
      height="auto"
      data-testid="navbar"
    >
      <VContainer class="pa-0" fluid>
        <VRow>
          <VCol v-show="!scrolled" cols="auto" class="hidden-md-and-down pl-1 pr-0 py-2 ml-n3" align-self="center">
            <LayoutHeaderLogo :clickable="!isSubscription" class="d-print-none"/>
          </VCol>
          <VCol class="py-0">
            <!-- Mount DemonstratorHeader at mobile widths too, so its dialog still answers open-demofinder from CMS content -->
            <LayoutHeaderDemonstratorHeader v-if="isMobile && !isDemo" class="d-none" />
            <!-- Visible parts are switched by CSS (hidden-*): the server only guesses the breakpoint
                 (see plugins/vuetify-ssr-display.ts). The v-ifs on isMobile here only choose parts
                 that are hidden at that width or render after mount. -->
            <VRow v-if="links" v-show="!scrolled" data-testid="top-links" justify="end" class="hidden-md-and-down pt-1 pb-3">
              <!-- One rule for both surfaces: useHeaderTopLinks returns the role-dependent
                   entries in order; the mobile drawer (MobileMenu) consumes the same list. -->
              <HeaderTopLink
                v-for="link in headerTopLinks"
                :key="link.id"
                :label="link.label"
                :to="link.to"
                :external="link.external"
              />
              <!-- One instance owns the open-demofinder dialog; it renders only after mount, so the
                   breakpoint choosing it moves nothing. -->
              <LayoutHeaderDemonstratorHeader v-if="!isMobile && !isDemo" />
              <VCol v-if="isDemo" class="shrink" align-self="center">
                <VChip color="backgroundPage" size="small" variant="flat" style="pointer-events: none">
                  <span class="font-weight-medium text-uppercase font12">{{ $t('label.demonstratorContext') }}</span>
                </VChip>
              </VCol>
            </VRow>
            <VRow no-gutters class="nav-actions-row v2-gutters" :class="scrolled ? 'mt-0' : 'my-3 my-lg-0'">
              <!-- Only once the header has collapsed, when the desktop logo column is
                   hidden and the teal bar's Exit / Back (BaseLayout) has scrolled away. -->
              <LazyLayoutHeaderSubscriptionExit v-if="isSubscription && scrolled" color="primary" class="scrolled-overlay" data-testid="subscription-exit" />
              <BaseBackNav v-if="isProxyOrderCheckout && scrolled" class="scrolled-overlay" @click="emits('back')" />
              <template v-if="links">
                <!-- Hidden below lg by CSS, so the v-if dropping it there once the viewport is measured
                     moves nothing; phones never render it, and a wrong desktop guess server-renders it
                     but never hydrates it (the media query), so neither loads its chunks. -->
                <VCol v-if="!isMobile" cols="auto" class="mr-auto flex-grow-1 hidden-md-and-down nav-menu-col" :align-self="scrolled ? 'center' : 'end'">
                  <template v-for="category in topNavCategories" :key="category.id">
                    <LazyMediaQueryLayoutHeaderMenuButton
                        :hydrate-on-media-query="DESKTOP_NAV_MEDIA"
                        :button-text="category.name"
                        :is-demo="!!isDemo"
                        :categories="category.children ?? []"
                        :base-category="category"
                        @click="handleCategoryClick(category)"
                    />
                  </template>
                  <LazyMediaQueryLayoutHeaderSparkitLogo
                    :hydrate-on-media-query="DESKTOP_NAV_MEDIA"
                    :categories="categories"
                    test-id="sparkit-logo-desktop"
                  />
                </VCol>
                <VCol class="shrink hidden-lg-and-up nav-hamburger pr-2" align-self="center">
                  <SBtn
                      data-testid="nav-hamburger"
                      width="32"
                      link-simple
                      density="compact"
                      :aria-label="$t('mobilenav.hamburger.aria-label')"
                      @click.stop="isSideMenuOpen = !isSideMenuOpen"
                  >
                    <VIcon
                        size="32"
                        color="black"
                    >
                      {{ icons.mdiMenu }}
                    </VIcon>
                  </SBtn>
                  <LayoutHeaderMenuMobileMenu
                    v-if="isSideMenuOpen"
                    :categories="topNavCategories"
                    @close="isSideMenuOpen = false"
                  />
                </VCol>
              </template>
              <VCol
                  class="hidden-lg-and-up pa-0 pr-sm-4"
                  align-self="center"
                  :class="{ 'logo--center': isSubscription || isProxyOrderCheckout }">
                <VContainer
                    class="typeset-logo pa-0"
                >
                  <SBtn
                      :disabled="isSubscription"
                      :to="logoLink"
                      text
                  >
                    <SBlankablePicture :media="TYPESET_LOGO_BLANK_MEDIA">
                      <img
                          src="@/assets/images/SU_LOGO_typeset.png"
                          alt="typeset logo"
                          class="img"
                      >
                    </SBlankablePicture>
                  </SBtn>
                </VContainer>
              </VCol>
              <VCol
                  v-if="search"
                  cols="auto"
                  :lg="isDemo ? 3 : 4"
                  order="5"
                  order-sm="1"
                  class="search flex-grow-1 pa-0 px-sm-2 ml-lg-3 color-customer pt-4 pt-sm-1 pt-lg-0"
                  :class="{ 'search--demo': isDemo, 'flex-lg-grow-0': !isDemo }"
                  align-self="center"
              >
                <LazyLayoutHeaderSearchTerms />
              </VCol>

              <VCol v-if="userMenu" class="shrink hidden-md-and-down px-2 pr-0" order="1" align-self="center">
                <AccountUserMenu />
              </VCol>
              <VCol v-if="isDemo && events" class="shrink hidden-md-and-up nav-icon-col" align-self="center" order="2">
                <EventsNavButton data-testid="eventsNav" :expanded="showEventsNav" @toggle="toggleEventsNav" />
              </VCol>
              <VCol v-if="canSeeRewards && loyalty" class=" shrink nav-icon-col" order="2"  align-self="center" >
                <LazyBaseLoyaltyRewardsHeader />
              </VCol>

              <!-- As nuxt2: a demonstrator has no favourites below md, and the whole column goes,
                   not just the heart, so its padding leaves no gap before the cart. -->
              <VCol
                class=" shrink nav-icon-col"
                :class="{ 'hidden-sm-and-down': isDemo }"
                align-self="center"
                order="3"
                order-sm="auto"
                data-testid="headerToolbarSection"
              >
                <NavHeart
                    v-if="wishlist"
                    size="24"
                    color="primary"
                    @on-login="triggerLogin"
                />
              </VCol>
              <VSpacer v-if="cart && !userMenu" class="hidden-sm-and-down" />
              <VCol class="cart-column shrink nav-icon-col" align-self="center" order="4" order-sm="auto">
                <CartTotalQty
                    v-if="cart"
                    data-testid="cart-qty"
                    @mouseenter="onCartMouseEnter"
                    @mouseleave="onCartMouseLeave"
                    @clicked-cart="handleClickedCart"
                />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </VContainer>

    </VToolbar>
    <!-- Demonstrator Events panel at xs/sm (nuxt2 parity). AB#63680 -->
    <EventsOverlay v-if="showEventsNav" @close="showEventsNav = false" />
  </VRow>
</template>

<script lang="ts" setup>
import { mdiMagnify, mdiMenu } from '@mdi/js'
import type { CategoryItem } from '~/types/root'

import CartTotalQty from '~/components/cart/CartTotalQty.vue'
import NavHeart from '~/components/layout/header/FavoritesButton.vue'
import EventsNavButton from '~/components/portal/events/EventsNavButton.vue'
import EventsOverlay from '~/components/layout/header/EventsOverlay.vue'

import { NotificationType } from '~/types/notifications'
import { useCartStore } from '~/stores/cart'
import { useAuthStore } from '~/stores/auth'
import { useRootStore } from '~/stores/root'
import { useNotificationsStore } from '~/stores/notifications'
import { useBreakpoints } from '~/composables/display'
import { minWidthQuery } from '~/config/breakpoints'
import { TYPESET_LOGO_BLANK_MEDIA } from '~/utils/format/imageSizes'
import { useHeaderTopLinks } from '~/composables/useHeaderTopLinks'
import HeaderTopLink from "~/components/layout/header/HeaderTopLink.vue";
import { PORTAL_ROOT } from '~/utils/portalRoutes'

const emits = defineEmits<{
  (e: 'clicked-cart' | 'back'): void
}>()
const icons = { mdiMagnify, mdiMenu }

interface Props {
  cart?: boolean
  wishlist?: boolean
  loyalty?: boolean
  events?: boolean
  links?: boolean
  search?: boolean
  isSubscription?: boolean
  isProxyOrderCheckout?: boolean
  userMenu?: boolean
  staticHeader?: boolean
}

withDefaults(defineProps<Props>(), {
  cart: true,
  wishlist: true,
  loyalty: true,
  events: true,
  links: true,
  search: true,
  isSubscription: false,
  isProxyOrderCheckout: false,
  userMenu: true,
  staticHeader: false
})

const authStore = useAuthStore()
const cartStore = useCartStore()
const rootStore = useRootStore()
const notificationsStore = useNotificationsStore()

const $i18n = useI18n()
const $t = (key: string) => $i18n.ts(key)
const { breakpoints } = useBreakpoints()
const layers = useHeaderLayers()
const { activeLayer } = layers

// Header treats "mobile" as md-and-down (≤ 1263px) — same as nuxt2's
// `$vuetify.breakpoint.mdAndDown`. The shared `useBreakpoints().isMobile`
// resolves to ≤ sm (< 600px) via the `mobileBreakpoint: 'sm'` config, which
// is too narrow for the header (tablet would lose the hamburger / get the
// desktop nav). Local override keeps the rest of the app's isMobile intact.
// The same lg boundary is written as isMobile, the hidden-md-and-down /
// hidden-lg-and-up classes, the md-and-down / lg-and-up style blocks,
// DESKTOP_NAV_MEDIA, and the logos' HEADER_LOGO_BLANK_MEDIA /
// TYPESET_LOGO_BLANK_MEDIA; moving it means moving all of them.
const isMobile = computed(() => breakpoints.mdAndDown.value)
// The desktop nav hydrates (and loads its chunks) only where it is shown.
const DESKTOP_NAV_MEDIA = minWidthQuery('lg')

// Cart hover flyout (parity with nuxt2): drive the shared useCartNotification timing
// (open 100ms), gated on hover capability AND non-mobile viewport so touch / tablet-
// sized screens get "tap cart -> navigate", no hover flyout.
const { openCartStateNotification, closeCartStateNotification, cleanup: cleanupCartNotification } = useCartNotification({
  openDelay: 100,
  enableTimerManagement: true
})
// The cart flyout uses the any-pointer query (see useHoverCapability): on a hybrid
// touchscreen laptop a mouse user keeps the flyout. Pure touch and pure mouse are
// unaffected. The `!isMobile` gate below still keeps tablet-sized screens on
// "tap cart -> navigate".
const { anyPointerHover: supportsHover } = useHoverCapability()
const enableHoverFlyout = computed(() => supportsHover.value && !isMobile.value)
const onCartMouseEnter = () => {
  if (!enableHoverFlyout.value) { return }
  openCartStateNotification()
}
const onCartMouseLeave = () => {
  if (!enableHoverFlyout.value) { return }
  closeCartStateNotification()
}
// Click on the cart icon closes any open cart flyout and cancels pending timers so
// they don't race with navigation (touch/trackpad emulates enter -> click -> leave).
const handleClickedCart = () => {
  cleanupCartNotification()
  if (notificationsStore.notification?.type === NotificationType.CartState) {
    notificationsStore.setHovered(false, 'cart')
  }
  emits('clicked-cart')
}
onBeforeUnmount(() => {
  cleanupCartNotification()
})

const categories = computed(() => rootStore.categories ?? [])
const { canSeeRewards } = useRewardsVisibility()
const { isDemo } = useSAuthState()
const headerTopLinks = useHeaderTopLinks()

const hideMenuItemClasses = computed(() => isDemo.value ? ['d-demo-none', 'd-none'] : ['d-none'])
const topNavCategories = computed(() => categories.value?.filter(c => c.topNav && !hideMenuItemClasses.value.includes(c.style ?? '')))
const logoLink = computed(() => {
  // PORTAL_ROOT is a path served outside the SPA (legacy demonstrator portal);
  // there is no vue-router route NAMED 'portal', so resolving { name: 'portal' }
  // throws "No match for {name:'portal'}" during SSR -> 500 for demo sessions.
  // Use the path string (matches the PORTAL_REPORTS/PORTAL_LEADERS_LOUNGE links).
  return isDemo.value ? PORTAL_ROOT : { name: 'index' }
})



const triggerLogin = (redirectOnSuccess?: string) => {
  rootStore.redirectRoute = redirectOnSuccess
  rootStore.authMessage = 'wishlist'
  authStore.updateAuthMenu(true)
}

// Demonstrator mobile Events panel (nuxt2 parity). EventsOverlay closes itself on a
// page change or at md and up. The page scroll lock is owned here for both overlays
// (this panel and the mobile drawer) and follows their combined state, so closing one
// never unlocks the page behind the other. useScrollLock releases on scope disposal.
// AB#63680
const showEventsNav = ref(false)
const pageScrollLock = useScrollLock(import.meta.client ? ((document.scrollingElement as HTMLElement | null) ?? document.documentElement) : null)
const toggleEventsNav = () => { showEventsNav.value = !showEventsNav.value }
// Events is a header layer: opening it closes the account menu, the rewards flyout
// and the cart notification, and opening any of those closes it.
watch(showEventsNav, (open) => {
  if (open) {
    layers.open('events')
  } else {
    layers.close('events')
  }
})
watch(activeLayer, (layer) => {
  if (layer !== 'events' && showEventsNav.value) {
    showEventsNav.value = false
  }
})
onBeforeUnmount(() => layers.close('events'))

const isSideMenuOpen = ref(false)
watchEffect(() => { pageScrollLock.value = showEventsNav.value || isSideMenuOpen.value })
// Close the mobile drawer when the auth dialog opens. Without this the drawer
// stays mounted behind the dialog; the dialog itself is owned only by the
// non-mobile UserMenu instance (see UserMenu.vue `!mobileMenu` guards).
watch(() => authStore.authMenu, (open) => {
  if (open) { isSideMenuOpen.value = false }
})

// Scroll-collapse (parity with nuxt2): scrolling down past SCROLL_THRESHOLD
// collapses the sticky header — the desktop appLogo column and the top-links
// row hide via `v-show`, vertical padding tightens (`navbar--scrolled`). It
// only re-expands once scrolled back to the very top.
const scrolled = ref(false)
const SCROLL_THRESHOLD = 50
let lastScrollY = 0
let ignoreScrollUntil = 0

const handleScroll = () => {
  // Toggling `scrolled` changes the header height, which shifts page content
  // and fires another scroll event — without a cooldown that ping-pongs.
  if (Date.now() < ignoreScrollUntil) { return }
  // Don't toggle while the cart-state flyout is open: resizing the nav under
  // the cursor moves the cart icon and mouseenter/leave-loops the flyout.
  if (notificationsStore.notification?.type === NotificationType.CartState) { return }
  // An open dialog's block scroll strategy pins <html> with position: fixed,
  // which reads as scrollY 0 -- "back at the top" -- although the page has not
  // moved. Re-expanding then unmounted the collapsed header's Exit, and the
  // confirmation dialog it owns closed the moment it opened. AB#63988
  if (document.documentElement.classList.contains('v-overlay-scroll-blocked')) { return }
  const currentY = window.scrollY
  const goingDown = currentY > lastScrollY
  lastScrollY = currentY
  if (goingDown && !scrolled.value && currentY > SCROLL_THRESHOLD) {
    scrolled.value = true
    ignoreScrollUntil = Date.now() + 150
  } else if (!goingDown && scrolled.value && currentY === 0) {
    scrolled.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

// As nuxt2: a category with children only opens its menu; a childless one
// navigates, external slugs in a new tab. Internal slugs used to be a dead
// click. The target comes from resolveCategoryTarget, the same rule the menu's own
// links (MenuItemList) use. The DBWS login marker needs the menu's API redirect,
// so it is left to the menu. AB#63678
const router = useRouter()
const { buildCategoryUrl } = useCategoryUrl()
const handleCategoryClick = (category: CategoryItem | null) => {
  if (!category) { return }
  const target = resolveCategoryTarget(category, hasVisibleChildren(category), buildCategoryUrl)
  if (target.kind === 'external') {
    openNewWindow(target.url)
  } else if (target.kind === 'internal') {
    router.push(target.to)
  } else if (target.kind === 'action') {
    target.action()
  }
}

</script>

<style lang='scss' scoped>
@use '~/assets/scss/settings';
@use '~/assets/scss/lib/vars';
@use 'sass:map';

// The collapsed header's Back on the proxy-order checkout, nuxt2's scrolled
// overlay: pinned left, on the collapsed header's centre line. nuxt2 nudged it
// there with fixed offsets (top 4px / -4px at lg) tuned to Vuetify 2's header
// heights; in this header those clipped it (mobile) or rode it up (desktop).
.scrolled-overlay {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  // At desktop the checkout's collapsed header has nothing else in its row, so an
  // overlay would leave the row 20px tall with Back hanging off it. In flow, the
  // row sizes itself around Back, as nuxt2's 37px collapsed header did.
  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    position: static;
    transform: none;
    margin: -2px 0 -4px;
  }
}

span::after {
  height: 0;
  visibility: hidden;
  display: block;
  font-weight: 700;
  content: attr(title);
}

.nav-component {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  z-index: 1999999999;

  // The drawer is nested in this sticky stacking context. Raise the context,
  // not just the drawer, so it stays above the app-level scrim.
  &--drawer-open {
    z-index: vars.$drawer-z-index;
  }

  // The SparkIt landing page opens below the header and reveals it by scrolling back to the top.
  // A sticky header re-pins on any downward scroll, so it has to leave the sticky flow entirely.
  // `relative` rather than `static` keeps the containing block and the stacking context that
  // `--drawer-open` escalates. Reachable only at the document top by design — AC scenario 2 on
  // AB#62164; do not "fix" it with reveal-on-upward-scroll. AB#62164
  &--static {
    position: relative;
  }

  .navbar {
    z-index: 11;
    border-bottom: 1px solid rgb(var(--v-theme-border));
    background: white;
    transition: padding 0.2s ease;
    &.rewards-flyout-open :deep(.v-toolbar__content) {
      overflow: visible;
    }
    // The header search menu is attached inside the toolbar (SearchTerms'
    // menu-props attach, so a click on a suggestion is not "outside" the
    // field), and Vuetify 4's toolbar clips it with overflow:hidden. Vuetify 2's
    // did not. Let it overflow while the menu is in the DOM, as the rewards
    // flyout does above. AB#64561
    &:has(.v-autocomplete__content) :deep(.v-toolbar__content) {
      overflow: visible;
    }

    .v-btn:hover {
      text-decoration: underline;
    }

    // On mobile/tablet (md-and-down) the header is a multi-row stacked layout
    // that's taller than V4's default toolbar height. Force auto so the
    // toolbar grows to fit instead of clipping content via overflow:hidden.
    // (nuxt2 does the same: `.navbar--mobile { height: auto !important }`.)
    // By media query, not a class from the breakpoint: see the template comment.
    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
      height: auto !important;
      &.navbar--scrolled {
        padding-top: 12px;
        padding-bottom: 12px;
      }
    }
    // Phone only — when scrolled the search col needs its 12px restored so it
    // sits where the rest of the row does. Tablet stays untouched.
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
      &.navbar--scrolled .search {
        padding-top: 12px;
      }
    }
  }
  .search {
    :deep(.v-container) {
      &>.v-row {
        justify-content: flex-end;
        &>.v-col {
          max-width: 576px;
        }
      }
    }
  }

  // Desktop nav/search/cart row behaves as a single-line toolbar: it never
  // wraps, every column is pinned so the icons can't be pushed off, the search
  // absorbs the slack, and the spacing between nav items shrinks fluidly with
  // the viewport — so the long-word demo nav (NL/DE/FR) fits on one line without
  // a table of fixed breakpoints.
  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    .nav-actions-row {
      flex-wrap: nowrap;
      > .search { flex-shrink: 1; min-width: 0; } // search absorbs the slack
    }
    .nav-menu-col {
      flex-basis: auto;               // size nav to content, not Vuetify's basis:0
      padding-left: 8px;              // nuxt2 PR 22638 (was ml-n2 pl-0)
    }
    :deep(.nav-menu-btn) {
      white-space: nowrap;
      margin-right: clamp(2px, 0.5vw, 16px);   // inter-item gap shrinks with viewport
      padding-left: clamp(2px, 0.4vw, 16px);
      padding-right: clamp(2px, 0.4vw, 16px);
    }
    // Customer-only search width (nuxt2 PR 22638): never below a ~320px field,
    // grows with the viewport, capped at ~576px. The clamp is 16px larger because
    // min-width includes the column's px-sm-2 padding. After the `> .search` rule above,
    // which it ties on specificity, so it wins. Demo keeps its fluid width.
    //
    // In nuxt2 the column always settles at exactly that min-width: the nav column
    // takes the spare space and the search never grows (measured 358/403/538/592px
    // at 1280/1440/1920/2560). In V4 it grew to 853px at 1920 instead. No grow and a
    // zero basis reproduce nuxt2's widths exactly. The no-grow is the column's
    // `flex-lg-grow-0` class, not a declaration here: the `flex-grow-1` utility lives
    // in the vuetify-utilities layer, after app-components, so a declaration here
    // would lose on layer order, and `flex-lg-grow-0` comes later in that same layer.
    // AB#63678
    .search:not(.search--demo) {
      min-width: clamp(336px, 28vw, 592px);
      flex-basis: 0;
    }
  }

  // Fluid padding on the header icon columns (loyalty / wishlist / cart). The
  // clamp caps at 8px — identical to the px-2 these columns use on desktop — so
  // it's a no-op above ~667px and only tightens toward 4px/side as the viewport
  // narrows, keeping the icon cluster on one row. No media query needed since
  // the clamp is self-limiting.
  .nav-icon-col {
    padding-left: clamp(4px, 1.2vw, 8px);
    padding-right: clamp(4px, 1.2vw, 8px);
  }
  .logo--center {
    width: 100%;
    .typeset-logo {
      display: flex;
      justify-content: center;
    }
  }
  :deep(.v-toolbar__content) {
    // Desktop unscrolled: the toolbar carries 4px y-padding. Combined with the
    // logo column's py-2 (16px) this totals the 24px of vertical rhythm nuxt2
    // gets from a flat 12/12 toolbar padding — matching qa's ~92px navbar.
    // (AB#61442: without this the toolbar was flush at 0 and the navbar floored
    // at ~84px, 8px short of qa.)
    padding: 4px 0;
    height: auto !important;
  }
  @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
    :deep(.v-toolbar__content) {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
  // Scrolled desktop only — y-padding inside the toolbar. 10/10 lands the
  // scrolled navbar at ~54px vs qa's 53px (12/12 overshot to 58px — AB#61442);
  // the residual 1px is sub-pixel rounding and matches nuxt2's own 10/10.
  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    .navbar--scrolled :deep(.v-toolbar__content) {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
    font-size: 1rem;

    .img {
      height: 16px;
      width: 164px;
    }
  }

  @media #{map.get(settings.$display-breakpoints, 'xs')} {
    .search {
        left: 0px;
        width: 100%;
        transition: top 0.1s ease-in-out 0s, left 0.2s ease-out 0s, width 0.2s ease-in-out 0s;
    }

  }
  :deep(.menu-btn > .v-btn__overlay) {
    opacity: 0;
  }
}

// Add smooth transitions for menu changes
:deep(.side-menu-enter-active),
:deep(.side-menu-leave-active) {
  transition: opacity 0.15s ease;
}

:deep(.side-menu-enter-from),
:deep(.side-menu-leave-to) {
  opacity: 0;
}

:deep(.side-menu-enter-to),
:deep(.side-menu-leave-from) {
  opacity: 1;
}


</style>
